import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';

import {
  CHECKOUT_ADDONS_STORAGE_KEY,
  CheckoutAddonSelection,
  addonDisplayCodeToApiCode,
  readCheckoutAddonsFromStorage,
  clearCheckoutAddonsStorage,
} from '../../utils/checkoutAddons';

import '../plans/plans.scss';

import bannerImg1 from '../../assets/images/BannerCoreVitalsMembership.png';
import bannerImg2 from '../../assets/images/BannerCoreVitalsPlusMembership.png';
import bannerImg3 from '../../assets/images/BannerFullBodyMRI.png';
import checkIcon from '../../assets/icons/CheckTick.svg';
import checkIconWhite from '../../assets/icons/CheckTickWhite.svg';

const ADDONS = [
  {
    id: 'amh',
    code: 'AMH',
    perTestAmount: 75,
    subtitle: 'Anti-Müllerian Hormone',
    description: 'Measures ovarian reserve and reproductive health.',
    priceLabel: '+£75 per test',
    options: [
      {
        id: 'include-all',
        label: 'Include in all my tests ([frequency] per year -  £[75 × frequency]/year or £6/month)',
      },
      {
        id: 'one-time',
        label: 'One-time addition only (£75 - will not be included in future tests)',
      },
    ],
  },
  {
    id: 'cea',
    code: 'CEA',
    perTestAmount: 50,
    subtitle: 'Carcinoembryonic Antigen',
    description: 'Tumor marker used to monitor certain cancers and inflammatory conditions.',
    priceLabel: '+£50 per test',
    options: [
      {
        id: 'include-all',
        label: 'Include in all my tests ([frequency] per year — £[50 × frequency]/year or £5/month)',
      },
      {
        id: 'one-time',
        label: 'One-time addition only (£50 — will not be included in future tests)',
      },
    ],
  },
  {
    id: 'ca19-9',
    code: 'CA19-9',
    perTestAmount: 45,
    subtitle: 'Cancer Antigen 19-9',
    description: 'Tumor marker primarily used to monitor pancreatic and digestive system cancers.',
    priceLabel: '+£45 per test',
    options: [
      {
        id: 'include-all',
        label: 'Include in all my tests ([frequency] per year - £[45 × frequency]/year or £4/month)',
      },
      {
        id: 'one-time',
        label: 'One-time addition only (£45 - will not be included in future tests)',
      },
    ],
  },
  {
    id: 'bnp',
    code: 'BNP',
    perTestAmount: 95,
    subtitle: 'B-type Natriuretic Peptide',
    description: 'Measures how hard your heart is working and indicates heart stress.',
    priceLabel: '+£95 per test',
    options: [
      {
        id: 'include-all',
        label: 'Include in all my tests ([frequency] per year - £[95 × frequency]/year or £8/month)',
      },
      {
        id: 'one-time',
        label: 'One-time addition only (£95 - will not be included in future tests)',
      },
    ],
  },
] as const;

type PlanType = 'coreVitals' | 'coreVitalsPlus' | 'fullBody' | '';
type PaymentType = 'monthly' | 'yearly';
type SelectedPlanData = {
  type: PlanType;
  testCount: number | null;
  product: {
    name?: string | null;
    monthly_amount?: number | null;
    yearly_amount?: number | null;
  } | null;
  productId: string | null;
  paymentType: PaymentType;
};

const formatPounds = (amount: number): string => {
  if (Number.isInteger(amount)) {
    return `£${amount}`;
  }
  return `£${amount.toFixed(2)}`;
};


const readSignupStoredPricing = (): { amount: number; promocode?: string } | null => {
  try {
    const rawSignup = localStorage.getItem('signupResponse');
    if (rawSignup) {
      const data = JSON.parse(rawSignup) as { amount?: unknown; promocode?: string };
      if (data.amount !== undefined && data.amount !== null) {
        const n = Number(data.amount);
        if (!Number.isNaN(n)) {
          return {
            amount: n,
            promocode:
              typeof data.promocode === 'string' && data.promocode.trim()
                ? data.promocode.trim()
                : undefined,
          };
        }
      }
    }
    const subStr = localStorage.getItem('subscriptionAmount');
    if (subStr !== null && subStr !== '') {
      const n = Number(subStr);
      if (!Number.isNaN(n)) {
        const pc = localStorage.getItem('appliedPromocode');
        return {
          amount: n,
          promocode: pc?.trim() || undefined,
        };
      }
    }
  } catch {
    /* ignore */
  }
  return null;
};

const PLAN_DETAILS: Record<Exclude<PlanType, ''>, { title: string; description: string; bannerImg: string; list: string[] }> = {
  coreVitals: {
    title: 'CoreVitals Membership',
    description: 'Ongoing health tracking without imaging - up to 4x blood tests/year',
    bannerImg: bannerImg1,
    list: [
      '1 blood biomarker test per year',
      'Includes free nurse blood draw at home',
      'Full biomarker panel',
      'Doctor review + consult',
      'Nutritionist guidance',
      'App access to track progress',
    ],
  },
  coreVitalsPlus: {
    title: 'CoreVitals+ Membership',
    description: 'CoreVitals+ Membership – Deeper insight across bloods combined with MRI imaging.',
    bannerImg: bannerImg2,
    list: [
      'Annual full-body MRI',
      'Pre- and post-scan clinician consults',
      'Combined blood and MRI insights in-app',
      'Ongoing tracking and progress monitoring',
    ],
  },
  fullBody: {
    title: 'Full-body MRI',
    description: 'A one-time internal snapshot',
    bannerImg: bannerImg3,
    list: [
      'In-app full-body scan report',
      'Doctor review + consult',
      'Pre and post scan clinician consults',
      'Report organised by body part',
      '60-90 minutes in the scanner',
    ],
  },
};

const BloodOnlyCheckout: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [openAddonOptions, setOpenAddonOptions] = useState<Record<string, boolean>>({});
  const [selectedAddonOption, setSelectedAddonOption] = useState<Record<string, string | undefined>>(
    {}
  );
  useEffect(() => {
    // Retain add-on selections when user navigates back from payment page.
    // We persist the API payload shape in storage; here we rehydrate UI state.
    const saved = readCheckoutAddonsFromStorage();
    if (!saved || saved.length === 0) return;

    const apiCodeToAddonId: Record<string, (typeof ADDONS)[number]['id']> = {};
    for (const addon of ADDONS) {
      apiCodeToAddonId[addonDisplayCodeToApiCode(addon.code)] = addon.id;
    }

    setSelectedAddonOption((prev) => {
      const next: Record<string, string | undefined> = { ...prev };
      for (const sel of saved) {
        const addonId = apiCodeToAddonId[(sel.code || '').toUpperCase()];
        if (!addonId) continue;
        next[addonId] = sel.selection_type === 'one_time' ? 'one-time' : 'include-all';
      }
      return next;
    });
  }, []);
  const selectedPlan = useMemo(() => {
    try {
      const raw = localStorage.getItem('selectedPlan');
      if (!raw) return null;
      return JSON.parse(raw) as SelectedPlanData;
    } catch {
      return null;
    }
  }, []);

  const signupStoredPricing = useMemo(() => readSignupStoredPricing(), []);

  const planType = selectedPlan?.type || 'coreVitals';
  const planDetails = PLAN_DETAILS[(planType || 'coreVitals') as Exclude<PlanType, ''>];
  const planTitle = selectedPlan?.product?.name || planDetails.title;
  const planList = useMemo(() => {
    const list = [...planDetails.list];
    const testCount = selectedPlan?.testCount ?? 0;
    if (testCount > 0 && (planType === 'coreVitals' || planType === 'coreVitalsPlus')) {
      return list.map((item) =>
        item.includes('blood biomarker test per year')
          ? `${testCount} blood biomarker test${testCount > 1 ? 's' : ''} per year`
          : item
      );
    }
    return list;
  }, [planDetails.list, planType, selectedPlan?.testCount]);

  const planPrice = useMemo(() => {
    const catalogAmount =
      selectedPlan?.paymentType === 'yearly'
        ? selectedPlan?.product?.yearly_amount
        : selectedPlan?.product?.monthly_amount;
    const amount =
      signupStoredPricing !== null ? signupStoredPricing.amount : catalogAmount;
    if (amount !== null && amount !== undefined) {
      const suffix = selectedPlan?.type === 'fullBody' ? '' : `/${selectedPlan?.paymentType === 'yearly' ? 'year' : 'month'}`;
      return `${formatPounds(amount)}${suffix}`;
    }
    return '~ £38/month';
  }, [selectedPlan, signupStoredPricing]);

  const billingFrequency = selectedPlan?.testCount && selectedPlan.testCount > 0 ? selectedPlan.testCount : 1;
  const isYearlyPlan = selectedPlan?.paymentType === 'yearly';
  const catalogCycleAmount =
    selectedPlan?.paymentType === 'yearly'
      ? selectedPlan?.product?.yearly_amount
      : selectedPlan?.product?.monthly_amount;


  const baseCycleAmount =
    signupStoredPricing !== null ? signupStoredPricing.amount : catalogCycleAmount ?? null;

  const selectedAddonsDetailed = useMemo(
    () =>
      ADDONS.flatMap((addon) => {
        const optionId = selectedAddonOption[addon.id];
        if (!optionId) return [];
        const optionLabel = optionId === 'one-time' ? 'once-off test' : 'twice a year';
        const recurringYearlyAmount = addon.perTestAmount * billingFrequency;
        const recurringCycleAmount = isYearlyPlan
          ? recurringYearlyAmount
          : recurringYearlyAmount / 12;
        return [
          {
            id: addon.id,
            code: addon.code,
            optionLabel,
            selectionType: optionId === 'one-time' ? 'one_time' : 'recurring',
            recurringCycleAmount,
            oneTimeAmount: addon.perTestAmount,
          },
        ];
      }),
    [billingFrequency, isYearlyPlan, selectedAddonOption]
  );

  const recurringAddonCycleTotal = useMemo(
    () =>
      selectedAddonsDetailed
        .filter((addon) => addon.selectionType === 'recurring')
        .reduce((sum, addon) => sum + addon.recurringCycleAmount, 0),
    [selectedAddonsDetailed]
  );

  const oneTimeAddonTotal = useMemo(
    () =>
      selectedAddonsDetailed
        .filter((addon) => addon.selectionType === 'one_time')
        .reduce((sum, addon) => sum + addon.oneTimeAmount, 0),
    [selectedAddonsDetailed]
  );

  const totalWithRecurringAddons =
    (baseCycleAmount ?? 0) + recurringAddonCycleTotal;
  const totalWithAllAddons = totalWithRecurringAddons + oneTimeAddonTotal;

  const displayTotal =
    baseCycleAmount !== null && baseCycleAmount !== undefined
      ? `${formatPounds(totalWithAllAddons)}${oneTimeAddonTotal > 0 || selectedPlan?.type === 'fullBody' ? '' : `/${isYearlyPlan ? 'year' : 'month'}`}`
      : planPrice;

  const renewalText = useMemo(() => {
    const now = new Date();
    const renewalDate = new Date(now);
    const isYearly = selectedPlan?.paymentType === 'yearly';
    renewalDate.setMonth(renewalDate.getMonth() + (isYearly ? 12 : 1));
    const formattedDate = renewalDate.toLocaleDateString('en-GB');

    const renewalPlanCycle =
      catalogCycleAmount !== null && catalogCycleAmount !== undefined
        ? catalogCycleAmount
        : baseCycleAmount;

    const amount =
      renewalPlanCycle !== null && renewalPlanCycle !== undefined
        ? renewalPlanCycle + recurringAddonCycleTotal
        : null;
    const amountLabel =
      amount !== null && amount !== undefined
        ? `${formatPounds(amount)}/${isYearly ? 'year' : 'month'}`
        : isYearly
          ? '£xxx/year'
          : '£xx/month';
    return `Subscription renews at ${amountLabel} on ${formattedDate}`;
  }, [baseCycleAmount, catalogCycleAmount, recurringAddonCycleTotal, selectedPlan?.paymentType]);

  const signupQuery = (() => {
    const token = searchParams.get('token');
    return token ? `?token=${encodeURIComponent(token)}` : '';
  })();

  const buildPayloadFromSelections = (): CheckoutAddonSelection[] =>
    ADDONS.flatMap((addon) => {
      const optionId = selectedAddonOption[addon.id];
      if (!optionId) return [];
      const selection_type = optionId === 'one-time' ? 'one_time' : 'recurring';
      return [
        {
          code: addonDisplayCodeToApiCode(addon.code),
          selection_type,
        },
      ];
    });

  const removeAddon = (addonId: string) => {
    setSelectedAddonOption((prev) => {
      const next = { ...prev };
      delete next[addonId];
      return next;
    });
  };

  const continueToSignup = (withAddons: boolean) => {
    if (withAddons) {
      const payload = buildPayloadFromSelections();
      if (payload.length === 0) return;
      localStorage.setItem(CHECKOUT_ADDONS_STORAGE_KEY, JSON.stringify(payload));
      sessionStorage.setItem(CHECKOUT_ADDONS_STORAGE_KEY, JSON.stringify(payload));
      navigate(`/complete-subscription${signupQuery}`, { state: { checkoutAddons: payload } });
      return;
    }
    clearCheckoutAddonsStorage();
    navigate(`/complete-subscription${signupQuery}`, { state: { checkoutAddons: [] } });
  };

  const selectedAddons = selectedAddonsDetailed;
  const hasSelectedAddons = selectedAddons.length > 0;

  return (
    <div className='c_container subscription-page-wrapper outer-blood-only-checkout google-sans-font-family'>
      <div className='two-colum-grid'>
        <div className='left-column'>
          <h2 className='select-plan-title'>Enhance your CoreVitals testing</h2>
          <div className='choose-between-monthly-yearly'>
          Add specialised biomarkers to track additional health indicators. Choose to add them to all your tests or as a one-time addition.
          </div>
          <div className='wrapper-blood-only-checkout' >
            {ADDONS.map((addon) => {
              const isOpen = !!openAddonOptions[addon.id];
              const selectedOptionId = selectedAddonOption[addon.id];
              const isAdded = !!selectedOptionId;
              const optionsWrapperId = `${addon.id}-options`;
              const radioName = `${addon.id}-options`;
              const recurringYearlyAmount = addon.perTestAmount * billingFrequency;
              const recurringMonthlyAmount = recurringYearlyAmount / 12;

              return (
                <div className='plan-card-wrapper' key={addon.id}>
                  <div className='plan-card-header d-flex' >
                    <div className='plan-card-header-info' >
                      <div className='' >
                        <div className='d-flex align-items-center gap-4' >
                          {addon.code} 
                        </div>

                        <div className='subtitle' >{addon.subtitle} </div>
                        <p>{addon.description}</p>
                      </div>
                      <div className='plan-card-header-price' >{addon.priceLabel}</div>
                    </div>
                  </div>

                  {isOpen && (
                    <div className='card-options-wrapper' id={optionsWrapperId}>
                      {addon.options.map((option) => {
                        const inputId = `${addon.id}-option-${option.id}`;
                        return (
                          <label htmlFor={inputId} key={option.id}>
                            <input
                              type="radio"
                              name={radioName}
                              id={inputId}
                              checked={selectedOptionId === option.id}
                              onChange={() =>
                                setSelectedAddonOption((prev) => ({ ...prev, [addon.id]: option.id }))
                              }
                            />
                            <span className='card-option-text'>
                              {option.id === 'include-all'
                                ? `Include in all my tests (${billingFrequency} per year - ${formatPounds(recurringYearlyAmount)}/year or ${formatPounds(recurringMonthlyAmount)}/month)`
                                : option.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  <div className='plan-card-footer' >
                    <div className='selected-option font-14' >
                      {selectedOptionId ? (
                        <span>{selectedOptionId === 'one-time' ? 'Once-off test' : 'Twice a year'}</span>
                      ) : ""}
                    </div>
                    {!isOpen && <button
                      className='btn'
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={optionsWrapperId}
                      onClick={() =>
                        setOpenAddonOptions((prev) => ({ ...prev, [addon.id]: !prev[addon.id] }))
                      }
                    >
                      Select options +
                    </button>}
                    {isOpen && (
                      <div className="addon-actions">
                        {isAdded && (
                          <button
                            type="button"
                            className="remove-addon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeAddon(addon.id);
                            }}
                          >
                            Remove <span aria-hidden="true">×</span>
                          </button>
                        )}
                        <button
                          className='btn'
                          type="button"
                          aria-expanded={isOpen}
                          aria-controls={optionsWrapperId}
                          // disabled={!isAdded}
                        >
                          {isAdded ? <span className='d-flex align-items-center gap-4' >Added <img width={10} height={8} src={checkIconWhite} alt='check' /></span> : 'Add +'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          {!isMobile && <div className='d-flex gap-16 footer-btn-wrapper' >
            <button
              type='button'
              className={`plans-step-btn no-addons ${hasSelectedAddons ? '' : 'disabled'}`}
              disabled={!hasSelectedAddons}
              onClick={() => continueToSignup(true)}
            >
              Continue with add-ons
            </button>
            <button type='button' className='plans-step-btn' onClick={() => continueToSignup(false)}>
              Continue without add-ons
            </button>
          </div>}
        </div>

        
          <div className='right-column'>
            <div className='inner-card'>
              <h4 className='inner-card-title'>Order summary</h4>
              <div className='plan-info'>
                <div className='order-summary-banner'>
                  <img src={planDetails.bannerImg} alt={`${planTitle} banner`} />
                </div>
                <div className='order-summary-title'>{planTitle}</div>
                <p className='order-summary-description'>{planDetails.description}</p>
                <ul className='order-summary-list'>
                  {planList.map((item) => (
                    <li key={item}>
                      <img src={checkIcon} alt='check' />
                      {item}
                    </li>
                  ))}
                  {selectedAddons.map((addon) => (
                    <li className='selected-option font-16' key={addon.id}>
                      <span className='add-on-price'>+</span>
                      {addon.code}, {addon.optionLabel}
                    </li>
                  ))}
                </ul>
                <div className='total-price'>
                  <div>Total</div>
                  <div>{displayTotal}</div>
                </div>
                <div className={`selected-option pt-8  ${isMobile ? 'font-16' : 'font-18'}`}>{renewalText}</div>
              </div>
            </div>
          </div>
        
      </div>
      {isMobile && <div className='d-flex gap-16 footer-btn-wrapper' >
            <button
              type='button'
              className={`plans-step-btn no-addons ${hasSelectedAddons ? '' : 'disabled'}`}
              disabled={!hasSelectedAddons}
              onClick={() => continueToSignup(true)}
            >
              Continue with add-ons
            </button>
            <button type='button' className='plans-step-btn' onClick={() => continueToSignup(false)}>
              Continue without add-ons
            </button>
          </div>}
    </div>
  );
};

export default BloodOnlyCheckout;

